<script setup lang="ts">
import FormattedNumberInput from '@/components/common/formatted-number-input.vue';
import InfoIcon from '@/components/common/info-icon.vue';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { forecastService } from '@/services/forecast/forecast-service';
import { notificationService } from '@/services/notification/notification-service';
import {
  ATAFixedColumns,
  ATAOngoingColumns,
  DetailATAFixedColumns,
  DetailATAFixedIsNotForecastedColumns,
  DetailATAOngoingColumns,
  ForecastColumns,
  ForecastDetailATAFixedColumns,
  ForecastDetailATAOngoingColumns,
  ForecastDetailColumns,
  SubATAFixedColumns,
  SubATAOngoingColumns,
  SubForecastColumns,
} from '@/utils/tables/forecast/column-definitions';

import type { ForecastColumnData } from '@/utils/tables/forecast/forecast-column-data';
import {
  getSortIndicator,
  sortData,
  toggleSortOrder,
} from '@/utils/tables/sorting-data';
import {
  PhArrowBendUpRight,
  PhFileArrowDown,
  PhPlus,
  PhTrash,
} from '@phosphor-icons/vue';
import {
  type ForecastData,
  ForecastDetailData,
  models,
  type Nullish,
  SubForecastData,
  SummarizedForecastData,
  utils,
} from 'cesg-shared';
import { ForecastType } from 'cesg-shared/dist/models';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

type EditState = { rowId: string | null; columnKey: string | null };

const route = useRoute();
const data = ref<ForecastData[]>([]);
const filteredData = ref<ForecastData[]>([]);
const beforeEditValue = ref<Nullish<string | number>>(null);
const loading = ref<boolean>(false);
const { t } = useI18n();
const editState = ref<EditState>({ rowId: null, columnKey: null });
const editInputRef = ref<HTMLInputElement | null>(null);
const expandedRows = ref<Set<string>>(new Set());
const subExpandedRows = ref<Set<string>>(new Set());
const isRowExpanded = (rowId: string) => expandedRows.value.has(rowId);
const isSubRowExpanded = (rowId: string) => subExpandedRows.value.has(rowId);
const showData = ref<boolean>(false);
const forecastColumns = ref<ForecastColumnData[]>([]);
const searchQuery = ref<string>('');
const sortState = ref<Record<number, 'none' | 'asc' | 'desc'>>({});
const dataChangedLoader = ref<boolean>(false);

interface Show {
  invoiceLink?: boolean;
  comments?: boolean;
}

interface ShouldDisable {
  comments?: boolean;
  accountTranslation?: boolean;
  inputs?: boolean;
}

const props = defineProps({
  forecastType: {
    type: String,
    required: true,
    default() {
      return models.ForecastType.Forecast;
    },
  },
  dataChanged: {
    type: Number,
    required: false,
    default: 0,
  },
  show: {
    type: Object as () => Show,
    required: false,
    default: () => ({
      invoiceLink: true,
      comments: true,
    }),
  },
  shouldDisable: {
    type: Object as () => ShouldDisable,
    required: false,
    default: () => ({
      comments: false,
      accountTranslation: false,
      inputs: false,
    }),
  },
});

export type ForecastTableEmits = {
  dataChanged: [];
};
const emit = defineEmits<ForecastTableEmits>();

const loadData = async () => {
  try {
    data.value = await forecastService.getAll(
      utils.type.asType<string>(route.params.id),
      props.forecastType,
    );
    filteredData.value = [...data.value];
  } finally {
    showData.value = true;
  }
};

const normalizeValue = (
  val: Nullish<
    string | number | boolean | SubForecastData[] | ForecastDetailData[]
  >,
):
  | number
  | string
  | null
  | boolean
  | SubForecastData[]
  | ForecastDetailData[] => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'string') {
    const num = Number(val.replace(/\s/g, ''));
    return isNaN(num) ? val : num;
  }
  return val;
};

const searchTable = () => {
  const query = searchQuery.value?.toLowerCase() || '';

  // Clear previously expanded rows
  expandedRows.value.clear();
  subExpandedRows.value.clear();

  filteredData.value = query
    ? data.value.filter((row) => {
        // Check if the parent row matches
        const rowMatch = forecastColumns.value.some((column) => {
          if (column.key && column.key in row) {
            const rawValue = row[column.key as keyof ForecastData];
            const formattedValue = column.format
              ? column.format(rawValue as Nullish<string | number>)
              : rawValue;

            return [rawValue, formattedValue]
              .filter((v) => v != null)
              .some((val) => val?.toString().toLowerCase().includes(query));
          }
          return false;
        });

        if (rowMatch) {
          return true;
        }

        // Check if any sub row matches
        const subRowMatch = row.rows?.some((subRow) => {
          const match = getSubColumn(row.type).some((subColumn) => {
            if (subColumn.key && subColumn.key in subRow) {
              const rawValue = subRow[subColumn.key as keyof SubForecastData];
              const formattedValue = subColumn.format
                ? subColumn.format(rawValue as Nullish<string | number>)
                : rawValue;

              return [rawValue, formattedValue]
                .filter((v) => v != null)
                .some((val) => val.toString().toLowerCase().includes(query));
            }
            return false;
          });

          if (match) {
            expandedRows.value.add(row.id); // Expand this row if subrow matches
          }
          return match;
        });

        if (subRowMatch) {
          return true;
        }

        // Check if any detail row matches
        const detailRowMatch = row.rows?.some((subRow) => {
          const match = subRow.rows?.some((detailRow) => {
            return getDetailColumn(
              row.type,
              subRow.type as string,
              row.ataFixedShouldBeForecasted,
            ).some((detailColumn) => {
              if (detailColumn.key && detailColumn.key in detailRow) {
                const rawValue =
                  detailRow[detailColumn.key as keyof ForecastDetailData];
                const formattedValue = detailColumn.format
                  ? detailColumn.format(rawValue as Nullish<string | number>)
                  : rawValue;

                return [rawValue, formattedValue]
                  .filter((v) => v != null)
                  .some((val) => val.toString().toLowerCase().includes(query));
              }
              return false;
            });
          });

          if (match) {
            expandedRows.value.add(row.id);
            subExpandedRows.value.add(subRow.id);
          }
          return match;
        });

        return detailRowMatch; // Include this row if any detail row matches
      })
    : [...data.value];
};

// Watch for changes to the search query
watch(searchQuery, () => {
  searchTable();
});

async function saveForecastEdit(row: ForecastData, column: string) {
  const currentValue = row[column as keyof typeof row];
  const previousValue = beforeEditValue.value;

  if (normalizeValue(currentValue) === normalizeValue(previousValue)) {
    editState.value = { rowId: null, columnKey: null };
    return;
  }
  loading.value = true;
  const isAccountTranslation = column === 'accountTranslation';
  const isComment = column === 'comment';
  try {
    if (isAccountTranslation) {
      const response = await forecastService.updateForecast(row.id, {
        accountTranslation:
          column === 'accountTranslation' ? row.accountTranslation : undefined,
      });
      notificationService.open({
        text: t('notifications.saveSuccess'),
        type: 'success',
      });
      Object.assign(row, response);
    }
    if (isComment) {
      const response = await forecastService.updateForecastComment(row.id, {
        comment: column === 'comment' ? (row.comment ?? undefined) : undefined,
      });
      notificationService.open({
        text: t('notifications.saveSuccess'),
        type: 'success',
      });
      Object.assign(row, response);
    }
    emit('dataChanged');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    (row[column as keyof typeof row] as typeof beforeEditValue.value) =
      beforeEditValue.value;
    beforeEditValue.value = null;
  } finally {
    editState.value = { rowId: null, columnKey: null };
    loading.value = false;
  }
}

async function saveSubForecastEdit(row: SubForecastData, column: string) {
  const currentValue = row[column as keyof typeof row];
  const previousValue = beforeEditValue.value;

  if (normalizeValue(currentValue) === normalizeValue(previousValue)) {
    editState.value = { rowId: null, columnKey: null };
    return;
  }
  loading.value = true;
  try {
    const response = await forecastService.updateSubforecast(row.id, {
      comment: row.comment,
    } as SubForecastData);
    notificationService.open({
      text: t('notifications.saveSuccess'),
      type: 'success',
    });
    Object.assign(row, response);
    emit('dataChanged');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    (row[column as keyof typeof row] as typeof beforeEditValue.value) =
      beforeEditValue.value;
    beforeEditValue.value = null;
  } finally {
    editState.value = { rowId: null, columnKey: null };
    loading.value = false;
  }
}

async function saveForecastDetailEdit(row: ForecastDetailData, column: string) {
  const currentValue = row[column as keyof typeof row];
  const previousValue = beforeEditValue.value;

  if (normalizeValue(currentValue) === normalizeValue(previousValue)) {
    editState.value = { rowId: null, columnKey: null };
    return;
  }
  loading.value = true;
  const isBudget = column === 'budget';
  const isForecast = column === 'forecast';
  const isResult = column === 'result';
  const isComment = column === 'comment';
  try {
    if (isBudget || isForecast || isResult) {
      const response = await forecastService.updateForecastdetail(row.id, {
        budget: isBudget ? row.budget : undefined,
        forecast: isForecast ? row.forecast : undefined,
        result: isResult ? row.result : undefined,
      });
      notificationService.open({
        text: t('notifications.saveSuccess'),
        type: 'success',
      });
      Object.assign(row, response);
    } else if (isComment) {
      const response = await forecastService.updateForecastDetailsComment(
        row.id,
        { comment: row.comment ?? undefined },
      );
      notificationService.open({
        text: t('notifications.saveSuccess'),
        type: 'success',
      });
      Object.assign(row, response);
    }
    emit('dataChanged');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    (row[column as keyof typeof row] as typeof beforeEditValue.value) =
      beforeEditValue.value;
    beforeEditValue.value = null;
  } finally {
    editState.value = { rowId: null, columnKey: null };
    loading.value = false;
  }
}

type KeyWithNumber<T> = Extract<keyof T, string>;

function getValue<T extends object>(
  row: T,
  key: KeyWithNumber<T> | '' | 'actions' | 'remaining',
  format?: (value: Nullish<string | number>) => Nullish<string | number>,
  defaultValue: string = '-',
): Nullish<string | number> {
  if (key === '' || key === 'actions') return '';

  let cellValue: Nullish<string | number>;

  // Specialfall för "remaining"
  if (key === 'remaining') {
    const forecast =
      'forecast' in row
        ? ((row['forecast'] as number | null | undefined) ?? 0)
        : 0;
    const result =
      'result' in row ? ((row['result'] as number | null | undefined) ?? 0) : 0;
    cellValue = forecast - result;
  } else if (key in row) {
    const value = row[key];
    if (value === '' || value === null || value === undefined) {
      cellValue = defaultValue;
    } else if (typeof value === 'string' || typeof value === 'number') {
      cellValue = value;
    } else {
      cellValue = defaultValue;
    }
  } else {
    cellValue = defaultValue;
  }

  return format ? format(cellValue as string | number) : cellValue;
}

function toggleRowExpansion(rowId: string) {
  if (isRowExpanded(rowId)) {
    expandedRows.value.delete(rowId);
    subExpandedRows.value.delete(rowId);
  } else {
    expandedRows.value.add(rowId);
  }
}

function toggleSubRowExpansion(rowId: string) {
  if (isSubRowExpanded(rowId)) {
    subExpandedRows.value.delete(rowId);
  } else {
    subExpandedRows.value.add(rowId);
  }
}

function saveCurrentValue(value: Nullish<string | number>) {
  beforeEditValue.value = value;
}

async function addForecastDetails(subRow: SubForecastData) {
  const response = await forecastService.newForecastDetails(subRow.id);
  subRow.rows.push(response);
  emit('dataChanged');
}

async function addPreviousForecastDetails(subRow: SubForecastData) {
  const response = await forecastService.newForecastDetails(subRow.id, true);
  subRow.rows.unshift(response);
  emit('dataChanged');
}

async function deleteForecast(
  parentSubRow: SubForecastData,
  forecastDetailsId: string,
) {
  try {
    await forecastService.deleteForecastDetails(forecastDetailsId);
    const index = parentSubRow.rows.findIndex(
      (row) => row.id === forecastDetailsId,
    );
    if (index !== -1) {
      parentSubRow.rows.splice(index, 1);
    }
    notificationService.open({
      text: t('forecast.forecastDeleted'),
      type: 'success',
    });
  } catch (error) {
    console.error(error);
  }
}

function openInvoices(subRow: SubForecastData) {
  const url = router.resolve({
    name: RouteName.Invoices,
    params: { id: route.params.id },
    query: {
      accountNumber: subRow.accountNumber ?? undefined,
      ataNumber: subRow.ataNumber ?? undefined,
    },
  }).href;
  window.open(url, '_blank');
}

function showLinkToInvoices(
  row: ForecastData,
  subRow: SubForecastData,
): boolean {
  return (
    (row.type === ForecastType.Forecast &&
      subRow.type !== ForecastType.AtaFixed &&
      subRow.type !== ForecastType.AtaOngoing) ||
    row.type === ForecastType.AtaOngoing ||
    row.type === ForecastType.AtaFixed
  );
}

function getSubColumn(forecastType: string) {
  if (forecastType === ForecastType.AtaOngoing) {
    return SubATAOngoingColumns;
  } else if (forecastType === ForecastType.AtaFixed) {
    return SubATAFixedColumns;
  }
  return SubForecastColumns;
}

function getDetailColumn(
  forecastType: string,
  subForecastType: string,
  ataFixedShouldBeForecasted: Nullish<models.YesNo>,
) {
  if (forecastType === ForecastType.Forecast) {
    if (subForecastType === ForecastType.AtaOngoing) {
      return ForecastDetailATAOngoingColumns;
    } else if (subForecastType === ForecastType.AtaFixed) {
      return ForecastDetailATAFixedColumns;
    }
  } else if (forecastType === ForecastType.AtaFixed) {
    if (ataFixedShouldBeForecasted === models.YesNo.No) {
      return DetailATAFixedIsNotForecastedColumns;
    } else return DetailATAFixedColumns;
  } else if (forecastType === ForecastType.AtaOngoing) {
    return DetailATAOngoingColumns;
  }
  return ForecastDetailColumns;
}

function getBudgetWarningText(key: string, subRow: SubForecastData): string {
  if (key === 'budget' && props.forecastType === ForecastType.AtaFixed) {
    if (subRow.aggregatedBudget === 0) {
      return t('forecast.table.missingBudget');
    } else if (subRow.aggregatedBudget !== subRow.budget) {
      return t('forecast.table.budgetMismatch');
    }
  }
  return ''; // No warning
}

async function exportToCSV() {
  overlaySpinnerService.show();
  try {
    const file = await forecastService.exportToCsv(
      utils.type.asType<string>(route.params.id),
      props.forecastType,
    );

    if (file) {
      const blob = new Blob([file], { type: 'text/csv' });
      const currentDate = new Date().toISOString().split('T')[0];

      const name =
        props.forecastType === ForecastType.Forecast
          ? 'prognos'
          : props.forecastType === ForecastType.AtaOngoing
            ? 'ata-lopande'
            : 'ata-fast';

      const fileName = `${name}-${currentDate}.csv`;
      utils.downloadFile(blob, fileName);
    } else {
      notificationService.open({
        text: t('notifications.exportToCsvError'),
        type: 'danger',
      });
    }
  } finally {
    overlaySpinnerService.hide();
  }
}

const toggleSortState = (colIndex: number) => {
  expandedRows.value.clear();
  subExpandedRows.value.clear();

  const currentState = sortState.value[colIndex];
  const newState = toggleSortOrder(currentState);

  Object.keys(sortState.value).forEach((key) => {
    if (Number(key) !== colIndex) {
      sortState.value[Number(key)] = 'none';
    }
  });

  sortState.value[colIndex] = newState;

  if (newState === 'none') {
    searchTable();
  } else {
    const colKey = forecastColumns.value[colIndex].key;
    filteredData.value = sortData(filteredData.value, colKey, newState);
  }
};

const summary = computed(
  () => new SummarizedForecastData(filteredData.value as ForecastData[]),
);

const summaryExclAta = computed(
  () => new SummarizedForecastData(filteredData.value as ForecastData[], false),
);

function shouldShowInput(key: string) {
  if (key !== 'comment') return true;
  return key === 'comment' && props.show.comments;
}

const disableOverrides = computed<Record<string, boolean | undefined>>(() => ({
  comment: props.shouldDisable.comments,
  accountTranslation: props.shouldDisable.accountTranslation,
}));

function shouldDisableInput(key: string) {
  return disableOverrides.value[key] ?? props.shouldDisable.inputs;
}

watch(
  () => props.dataChanged,
  async () => {
    dataChangedLoader.value = true;
    await loadData();
    dataChangedLoader.value = false;
  },
);

onMounted(async () => {
  overlaySpinnerService.show();
  await loadData();
  if (props.forecastType === models.ForecastType.Forecast) {
    forecastColumns.value = ForecastColumns;
  } else if (props.forecastType === models.ForecastType.AtaOngoing) {
    forecastColumns.value = ATAOngoingColumns;
  } else if (props.forecastType === models.ForecastType.AtaFixed) {
    forecastColumns.value = ATAFixedColumns;
  }
  forecastColumns.value.forEach((_, index) => {
    sortState.value[index] = 'none';
  });
  overlaySpinnerService.hide();
});

const filteredColumns = computed(() => {
  if (props.show.comments) {
    return forecastColumns.value;
  }
  return forecastColumns.value.filter((col) => col.key !== 'comment');
});
</script>
<template>
  <div class="field mt-4">
    <div class="control">
      <input
        class="input search"
        type="search"
        :placeholder="t('search') + '...'"
        v-model="searchQuery"
        data-test="search-input"
      />
    </div>
  </div>
  <div class="forecast-table">
    <div class="table-progress-bar" v-if="dataChangedLoader">
      <div class="progress-bar-inner"></div>
    </div>
    <table
      class="table is-fullwidth"
      :class="{
        'is-forecast': props.forecastType === models.ForecastType.Forecast,
        'is-ata-lopande': props.forecastType === models.ForecastType.AtaOngoing,
        'is-ata-fast': props.forecastType === models.ForecastType.AtaFixed,
      }"
    >
      <thead>
        <tr>
          <th
            v-for="(column, index) in filteredColumns"
            :key="index"
            :class="[{ sortable: column.sortable }, column.align, column.key]"
            :title="column.label"
            @click.prevent="column.sortable && toggleSortState(index)"
          >
            <span
              v-if="column.sortable"
              class="sort-indicator"
              :class="{
                'is-none': sortState[index] === 'none',
                'is-asc': sortState[index] === 'asc',
                'is-desc': sortState[index] === 'desc',
                'align-left': column.align === 'has-text-right',
                'align-right': column.align !== 'has-text-right',
              }"
            >
              {{ getSortIndicator(sortState[index]) }}
            </span>
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(row, rowIndex) in filteredData" :key="rowIndex">
          <tr
            @click.stop="toggleRowExpansion(row.id)"
            class="forecast-row"
            :class="{
              'is-missing-in-budget': row.isMissingInBudget,
              'has-children': row.rows.length > 0,
            }"
          >
            <td
              v-for="(column, columnIndex) in filteredColumns"
              :key="columnIndex"
              :class="[
                column.align,
                column.key,
                column.key && column.key !== 'actions'
                  ? column.class?.(row[column.key] as string)
                  : '',
              ]"
            >
              <template
                v-if="
                  column.editable &&
                  column.key !== '' &&
                  column.key !== 'actions'
                "
              >
                <div
                  class="control is-small"
                  :class="{ 'is-loading': loading }"
                  @click.stop
                >
                  <formatted-number-input
                    v-if="
                      column.editableInputType === 'number' &&
                      shouldShowInput(column.key)
                    "
                    :disabled="shouldDisableInput(column.key)"
                    v-model.lazy="
                      filteredData[rowIndex][
                        column.key as keyof ForecastData
                      ] as string | number | null
                    "
                    :id="`input-${row.id}-${column.key}`"
                    @blur="
                      saveForecastEdit(
                        filteredData[rowIndex] as ForecastData,
                        column.key as string,
                      )
                    "
                    @keyup.enter="($event.target as HTMLInputElement).blur()"
                    @focus.prevent="
                      saveCurrentValue(
                        filteredData[rowIndex][
                          column.key as keyof ForecastData
                        ] as string | number,
                      )
                    "
                    class="input is-small"
                    ref="editInputRef"
                    data-test="edit-forecast-data"
                    :lazy="true"
                  />
                  <textarea
                    v-else-if="shouldShowInput(column.key)"
                    :disabled="shouldDisableInput(column.key)"
                    v-model.lazy="
                      filteredData[rowIndex][
                        column.key as keyof ForecastData
                      ] as string
                    "
                    :id="`input-${row.id}-${column.key}`"
                    @blur="
                      saveForecastEdit(
                        filteredData[rowIndex] as ForecastData,
                        column.key as string,
                      )
                    "
                    @focus.prevent="
                      saveCurrentValue(
                        filteredData[rowIndex][
                          column.key as keyof ForecastData
                        ] as string | number,
                      )
                    "
                    class="textarea is-small"
                    rows="1"
                    ref="editInputRef"
                    data-test="edit-forecast-data"
                    maxlength="180"
                  />
                </div>
              </template>
              <template v-else>
                <span>
                  <template
                    v-if="
                      column.key === 'previousForecast' ||
                      column.key === 'forecast'
                    "
                  >
                    <span
                      :class="{
                        'has-text-danger':
                          (Math.round(row[column.key] as number) ?? 0) <
                          (Math.round(row['result'] as number) ?? 0),
                      }"
                    >
                      {{
                        getValue(
                          row as ForecastData,
                          column.key,
                          column.format,
                        )
                      }}{{ column.prefix || '' }}
                    </span>
                  </template>
                  <template v-else>
                    {{ getValue(row as ForecastData, column.key, column.format)
                    }}{{ column.prefix || '' }}
                  </template>
                  <template v-if="column.key === 'name'">
                    <template v-if="row.isMissingInBudget">
                      <info-icon
                        type="danger"
                        :tooltip="t('forecast.table.missingAccount')"
                        data-test="missing-in-budget"
                      ></info-icon>
                    </template>
                    <template
                      v-if="row.ataFixedShouldBeForecasted === models.YesNo.Yes"
                    >
                      <info-icon
                        type="warning"
                        :tooltip="t('forecast.table.ataForecasted')"
                        data-test="is-forecasted"
                      ></info-icon>
                    </template>
                  </template>
                </span>
              </template>
            </td>
          </tr>

          <template v-if="expandedRows.has(row.id)">
            <template v-for="(subRow, subIndex) in row.rows" :key="subRow.id">
              <tr
                class="expanded-row"
                @click.stop="toggleSubRowExpansion(subRow.id)"
                :class="{ 'has-children': subRow.rows.length > 0 }"
              >
                <td
                  v-for="(subColumn, subColumnIndex) in getSubColumn(row.type)"
                  :key="'expanded-' + subIndex + '-' + subColumnIndex"
                  :class="[
                    subColumn.align,
                    subColumn.key,
                    subColumn.key && subColumn.key !== 'actions'
                      ? subColumn.class?.(
                          subRow[subColumn.key] as string | number,
                        )
                      : '',
                  ]"
                >
                  <template
                    v-if="
                      subColumn.editable &&
                      subColumn.key !== '' &&
                      subColumn.key !== 'actions'
                    "
                  >
                    <div
                      class="control is-small"
                      :class="{ 'is-loading': loading }"
                      @click.stop
                    >
                      <textarea
                        v-if="shouldShowInput(subColumn.key)"
                        :disabled="shouldDisableInput(subColumn.key)"
                        v-model.lazy="
                          filteredData[rowIndex].rows[subIndex][
                            subColumn.key as keyof SubForecastData
                          ] as string
                        "
                        :id="`input-${subRow.id}-${subColumn.key}`"
                        @blur="
                          saveSubForecastEdit(
                            subRow as SubForecastData,
                            subColumn.key as string,
                          )
                        "
                        @focus.prevent="
                          saveCurrentValue(
                            filteredData[rowIndex].rows[subIndex][
                              subColumn.key as keyof SubForecastData
                            ] as string | number,
                          )
                        "
                        class="textarea is-small"
                        rows="1"
                        ref="editInputRef"
                        data-test="edit-subForecast-data"
                        maxlength="180"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <span>
                      <template
                        v-if="
                          subColumn.key === 'previousForecast' ||
                          subColumn.key === 'forecast'
                        "
                      >
                        <span
                          :class="{
                            'has-text-danger':
                              (Math.round(subRow[subColumn.key] as number) ??
                                0) <
                              (Math.round(subRow['result'] as number) ?? 0),
                          }"
                        >
                          {{
                            getValue(
                              subRow as SubForecastData,
                              subColumn.key,
                              subColumn.format,
                            )
                          }}{{ subColumn.prefix || '' }}
                        </span>
                      </template>
                      <template v-else>
                        {{
                          getValue(
                            subRow as SubForecastData,
                            subColumn.key,
                            subColumn.format,
                          )
                        }}{{ subColumn.prefix || '' }}
                      </template>
                      <info-icon
                        :tooltip="
                          getBudgetWarningText(
                            subColumn.key,
                            subRow as SubForecastData,
                          )
                        "
                        v-if="
                          getBudgetWarningText(
                            subColumn.key,
                            subRow as SubForecastData,
                          )
                        "
                        type="warning"
                        data-test="budget-warning"
                      />
                    </span>
                  </template>
                </td>
              </tr>
              <!-- Spacer row for non-expanded last sub-row -->
              <tr
                v-if="
                  subIndex === row.rows.length - 1 &&
                  !subExpandedRows.has(subRow.id)
                "
                class="spacer-row"
              >
                <td :colspan="filteredColumns.length"></td>
              </tr>
              <template v-if="subExpandedRows.has(subRow.id)">
                <tr v-if="!shouldDisableInput('actions')" class="actions">
                  <td>
                    <button
                      data-test="add-row-before"
                      class="button is-small is-text"
                      @click="
                        addPreviousForecastDetails(subRow as SubForecastData)
                      "
                    >
                      <ph-plus size="14" weight="bold" class="mr-1"></ph-plus>
                      {{ t('forecast.table.addForecastDetailsRowButton') }}
                    </button>
                  </td>
                  <td
                    v-for="item in filteredColumns.length - 1"
                    :key="item"
                  ></td>
                </tr>
                <template
                  v-for="(forecastDetail, forecastDetailIndex) in subRow.rows"
                  :key="forecastDetail.id"
                >
                  <tr class="details-row">
                    <td
                      v-for="(
                        detailsColumn, detailsColumnIndex
                      ) in getDetailColumn(
                        row.type,
                        subRow.type as string,
                        row.ataFixedShouldBeForecasted,
                      )"
                      :key="
                        'sub-expanded-' +
                        forecastDetailIndex +
                        '-' +
                        detailsColumnIndex
                      "
                      :class="[
                        detailsColumn.align,
                        detailsColumn.key,
                        detailsColumn.key && detailsColumn.key !== 'actions'
                          ? detailsColumn.class?.(
                              forecastDetail[detailsColumn.key] as string,
                            )
                          : '',
                      ]"
                    >
                      <template v-if="detailsColumn.key === 'actions'">
                        <span
                          v-if="
                            forecastDetailIndex === 0 ||
                            forecastDetailIndex === subRow.rows.length - 1
                          "
                        >
                          <button
                            v-if="!shouldDisableInput('actions')"
                            type="button"
                            class="button is-small is-danger"
                            @click="
                              deleteForecast(
                                subRow as SubForecastData,
                                forecastDetail.id,
                              )
                            "
                            data-test="delete-btn"
                          >
                            <PhTrash :size="14" />
                          </button>
                        </span>
                      </template>
                      <template
                        v-else-if="
                          detailsColumn.editable?.(subRow.accountNumber) &&
                          detailsColumn.key !== ''
                        "
                      >
                        <div
                          class="control is-small"
                          :class="{ 'is-loading': loading }"
                          @click.stop
                        >
                          <formatted-number-input
                            v-if="
                              detailsColumn.editableInputType === 'number' &&
                              shouldShowInput(detailsColumn.key)
                            "
                            :disabled="shouldDisableInput(detailsColumn.key)"
                            v-model.lazy="
                              filteredData[rowIndex].rows[subIndex].rows[
                                forecastDetailIndex
                              ][
                                detailsColumn.key as keyof ForecastDetailData
                              ] as string | number | null
                            "
                            :id="`input-${forecastDetail.id}-${detailsColumn.key}`"
                            @blur="
                              saveForecastDetailEdit(
                                forecastDetail as ForecastDetailData,
                                detailsColumn.key as string,
                              )
                            "
                            @keyup.enter="
                              ($event.target as HTMLInputElement).blur()
                            "
                            @focus.prevent="
                              saveCurrentValue(
                                filteredData[rowIndex].rows[subIndex].rows[
                                  forecastDetailIndex
                                ][
                                  detailsColumn.key as keyof ForecastDetailData
                                ] as string | number,
                              )
                            "
                            class="input is-small"
                            ref="editInputRef"
                            data-test="edit-forecast-detail"
                            :lazy="true"
                          />
                          <textarea
                            v-else-if="shouldShowInput(detailsColumn.key)"
                            :disabled="shouldDisableInput(detailsColumn.key)"
                            v-model.lazy="
                              filteredData[rowIndex].rows[subIndex].rows[
                                forecastDetailIndex
                              ][
                                detailsColumn.key as keyof ForecastDetailData
                              ] as string
                            "
                            :id="`input-${forecastDetail.id}-${detailsColumn.key}`"
                            @blur="
                              saveForecastDetailEdit(
                                forecastDetail as ForecastDetailData,
                                detailsColumn.key as string,
                              )
                            "
                            @focus.prevent="
                              saveCurrentValue(
                                filteredData[rowIndex].rows[subIndex].rows[
                                  forecastDetailIndex
                                ][
                                  detailsColumn.key as keyof ForecastDetailData
                                ] as string | number,
                              )
                            "
                            class="textarea is-small"
                            rows="1"
                            ref="editInputRef"
                            data-test="edit-forecast-detail"
                            maxlength="180"
                          />
                        </div>
                      </template>
                      <template v-else>
                        <span>
                          {{
                            getValue(
                              forecastDetail as ForecastDetailData,
                              detailsColumn.key,
                              detailsColumn.format,
                            )
                          }}{{ detailsColumn.prefix || '' }}
                        </span>
                      </template>
                      <template v-if="detailsColumn.key === 'month'">
                        <template v-if="forecastDetail.outsideOriginalForecast">
                          <info-icon
                            type="danger"
                            :tooltip="
                              t('forecast.table.outsideOriginalForecast')
                            "
                            data-test="outside-original-forecast"
                          ></info-icon>
                        </template>
                      </template>
                    </td>
                  </tr>
                </template>
                <tr class="actions">
                  <td>
                    <button
                      v-if="!shouldDisableInput('actions')"
                      data-test="add-row"
                      class="button is-small is-text"
                      @click="addForecastDetails(subRow as SubForecastData)"
                    >
                      <ph-plus size="14" weight="bold" class="mr-1"></ph-plus>
                      {{ t('forecast.table.addForecastDetailsRowButton') }}
                    </button>
                    <button
                      data-test="open-invoices"
                      class="button is-small is-text"
                      @click="openInvoices(subRow as SubForecastData)"
                      v-if="
                        props.show.invoiceLink &&
                        showLinkToInvoices(
                          row as ForecastData,
                          subRow as SubForecastData,
                        )
                      "
                    >
                      <PhArrowBendUpRight
                        size="14"
                        weight="bold"
                        class="mr-1"
                      />
                      {{ t('forecast.table.invoices') }}
                    </button>
                  </td>
                  <td
                    v-for="item in filteredColumns.length - 1"
                    :key="item"
                  ></td>
                </tr>
                <!-- Spacer row for expanded last sub-row -->
                <tr v-if="subIndex === row.rows.length - 1" class="spacer-row">
                  <td :colspan="filteredColumns.length"></td>
                </tr>
              </template>
            </template>
          </template>
        </template>
      </tbody>
      <tfoot>
        <tr
          class="forecast-row"
          v-if="forecastType === ForecastType.Forecast"
          data-test="summary-excl-ata"
        >
          <td
            v-for="(column, index) in filteredColumns"
            :key="index"
            :class="[column.align ? column.align : '', column.key]"
          >
            <span v-if="index === 0">
              {{ t('sumExcludedAta') }}
            </span>

            <span
              v-else-if="
                (column.key as keyof SummarizedForecastData) in summaryExclAta
              "
            >
              <span v-if="column.format">
                {{
                  column.format(
                    summaryExclAta[column.key as keyof SummarizedForecastData],
                  )
                }}
              </span>
              <span v-else>
                {{ summaryExclAta[column.key as keyof SummarizedForecastData] }}
              </span>
            </span>
          </td>
        </tr>
        <tr class="forecast-row" data-test="summary-incl-ata">
          <td
            v-for="(column, index) in filteredColumns"
            :key="index"
            :class="[column.align ? column.align : '', column.key]"
          >
            <span v-if="index === 0">
              {{ t('sum') }}
            </span>

            <span
              v-else-if="
                (column.key as keyof SummarizedForecastData) in summary
              "
            >
              <span v-if="column.format">
                {{
                  column.format(
                    summary[column.key as keyof SummarizedForecastData],
                  )
                }}
              </span>
              <span v-else>
                {{ summary[column.key as keyof SummarizedForecastData] }}
              </span>
            </span>
          </td>
        </tr>
      </tfoot>
    </table>
    <button class="button is-text" @click="exportToCSV">
      <PhFileArrowDown class="mr-1" :size="22" />
      {{ t('exportToCsvButton') }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/style/variables';
@use 'bulma/sass/utilities/initial-variables' as *;

.forecast-table {
  // Let the table scroll with the window (not inside this wrapper) so the
  // sticky header below can stick to the top of the viewport. On narrow
  // screens the page scrolls horizontally instead of this wrapper.
  overflow-x: visible;
}

.table {
  min-width: 800px;

  thead th {
    // Sticky per-table header: each table's header sticks to the top of the
    // screen while that table is in view; scrolling into the next table lets
    // its header take over.
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: #ffffff;
    // Drop the collapsed native border (it scrolls away once the header is
    // stuck) and draw the divider as an inset shadow instead, which stays
    // attached to the sticky cell. Outset shadows on table cells are clipped
    // in border-collapse mode, so the divider must be inset. Matches the
    // overview tables' sticky-header divider.
    border-bottom: 0;
    box-shadow: inset 0 -2px 0 #a5a5a5;
    padding: 1rem 0.75rem;
  }

  th {
    &.name,
    &.comment,
    &.accountTranslation {
      width: 15%;
    }

    &.previousForecast,
    &.result,
    &.remaining,
    &.budgetLeft,
    &.earnings,
    &.budget,
    &.forecast {
      width: 10%;
    }

    &.actions {
      width: 6%;
      min-width: 60px;
    }
  }

  &.is-forecast {
    --bulma-table-cell-border-color: #ddd;

    tr.forecast-row {
      background-color: #ffffff;
      border-top: 2px solid #c9c9c9 !important;

      td {
        padding: 1rem 0.5rem;
        border-top: 2px solid #c9c9c9 !important;
      }
    }

    tr.expanded-row {
      background-color: #f5f5f5;
      font-size: 0.95rem;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 1.5rem, #f5f5f5 1.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }

    tr.details-row,
    tr.actions {
      background-color: #e8e8e8;
      --bulma-table-cell-border-color: #d0d0d0;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 3.5rem, #e8e8e8 3.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 3.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }
  }

  &.is-ata-lopande {
    --bulma-table-cell-border-color: #ddd;

    tr.forecast-row {
      background-color: #ffffff;

      td {
        border-top: 2px solid #c9c9c9 !important;
        padding: 1rem 0.5rem;
      }
    }

    tr.expanded-row {
      background-color: #f5f5f5;
      font-size: 0.95rem;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 1.5rem, #f5f5f5 1.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }

    tr.details-row,
    tr.actions {
      background-color: #e8e8e8;
      --bulma-table-cell-border-color: #d0d0d0;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 3.5rem, #e8e8e8 3.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 3.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }
  }

  &.is-ata-fast {
    --bulma-table-cell-border-color: #ddd;

    tr.forecast-row {
      background-color: #ffffff;

      td {
        border-top: 2px solid #c9c9c9 !important;
        padding: 1rem 0.5rem;
      }
    }

    tr.expanded-row {
      background-color: #f5f5f5;
      font-size: 0.95rem;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 1.5rem, #f5f5f5 1.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }

    tr.details-row,
    tr.actions {
      background-color: #e8e8e8;
      --bulma-table-cell-border-color: #d0d0d0;

      td {
        border-bottom-color: var(--bulma-table-cell-border-color);
      }

      td:first-child {
        background: linear-gradient(to right, #ffffff 3.5rem, #e8e8e8 3.5rem);
        border-left: none;
        position: relative;
        border-bottom: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 3.5rem;
          right: 0;
          height: 1px;
          background-color: var(--bulma-table-cell-border-color);
        }
      }
    }
  }

  tr {
    td {
      vertical-align: middle;
    }

    &.has-children {
      cursor: pointer;
    }

    &.expanded-row {
      td:first-child {
        padding-left: 2rem;
      }
    }

    &.details-row {
      font-size: 0.9rem;

      td:first-child {
        padding-left: 4rem;
      }

      input {
        width: 6rem;
      }

      &:hover {
        filter: brightness(0.95);
      }
    }

    &.actions {
      td:first-child {
        padding-left: 4rem;
      }
    }

    &.spacer-row {
      height: 1.5rem;
      background-color: #ffffff;

      td {
        padding: 0;
        height: 1.5rem;
        border: none !important;
      }
    }
  }

  tfoot {
    tr td {
      font-family: var(--font-primary-semibold);
    }
    tr:first-child td {
      border-top-width: var(--bulma-control-border-width);
    }
  }
}

.input.search {
  width: 20vw;
  @media screen and (max-width: $desktop) {
    width: 85vw;
  }
}

.table-progress-bar {
  position: relative;
  height: 4px;
  background-color: var(--light-grey);
  overflow: hidden;
  margin-bottom: -4px;

  .progress-bar-inner {
    position: absolute;
    height: 100%;
    width: 0;
    background-color: var(--primary-color);
    animation: progressAnimation 1.5s infinite;
  }
}

@keyframes progressAnimation {
  0% {
    left: -100%;
    width: 30%;
  }
  50% {
    left: 20%;
    width: 50%;
  }
  100% {
    left: 100%;
    width: 30%;
  }
}

// Styling for number inputs only
.input.is-small {
  font-size: 0.93rem;
  font-weight: inherit;
  font-family: inherit;
  padding: 0.25rem 0.2rem;
}

// Right-align number inputs
.has-text-right {
  .input.is-small {
    text-align: right;
  }
}

// Ensure number inputs in detail rows are right-aligned
tr.details-row {
  .input.is-small {
    text-align: right;
  }
}

.textarea:disabled {
  color: inherit;
  opacity: 1;
  -webkit-text-fill-color: currentColor;
}
</style>
